import { useState, useEffect, useMemo } from 'react';
import { DateRange } from 'react-day-picker';
import { subDays, isAfter, isBefore, parseISO } from 'date-fns';
import { loadJsonData } from '@/utils/jsonLoader';
import { toast } from 'sonner';

export interface DashboardData {
  incidents: any[];
  catalogTasks: any[];
}

export function useDashboardData(initialData?: { [key: string]: any[] }, jsonData?: any) {
  const [excelData, setExcelData] = useState<{ [key: string]: any[] }>(initialData || {});
  const [filteredData, setFilteredData] = useState<{ [key: string]: any[] }>({});
  const [isLoading, setIsLoading] = useState(!initialData);
  const [dataSource, setDataSource] = useState<'initial' | 'static' | 'json' | 'empty'>('initial');
  const [selectedApi, setSelectedApi] = useState<string>("all");
  const [timeRange, setTimeRange] = useState<string>("30");
  const [ticketType, setTicketType] = useState<string>("all");
  const [priority, setPriority] = useState<string>("all");
  const [assignmentGroup, setAssignmentGroup] = useState<string>("all");
  const [dateRange, setDateRange] = useState<DateRange | undefined>({
    from: subDays(new Date(), 30),
    to: new Date()
  });

  // Load data if not provided initially
  useEffect(() => {
    const loadData = async () => {
      // Check if we have valid JSON data
      if (jsonData && !jsonData.error) {
        const hasValidJsonData = 
          (jsonData.incidentsParsed?.length > 0 || 
           jsonData.catalogTasksParsed?.length > 0);
            
        if (hasValidJsonData) {
          console.log("✅ Using JSON data:", {
            incidents: jsonData.incidentsParsed?.length || 0,
            catalogTasks: jsonData.catalogTasksParsed?.length || 0
          });
          
          // Create a data structure similar to what we expect
          const jsonParsedData = {
            'Incidents': jsonData.incidentsParsed || [],
            'Catalog Tasks': jsonData.catalogTasksParsed || []
          };
          
          setExcelData(jsonParsedData);
          setFilteredData(jsonParsedData);
          setIsLoading(false);
          setDataSource('json');
          toast.success(`Loaded ${jsonParsedData['Incidents'].length} incidents and ${jsonParsedData['Catalog Tasks'].length} catalog tasks from JSON`);
          return;
        }
      }
      
      if (initialData && Object.keys(initialData).length > 0 && 
          initialData['Incidents']?.length > 0 && initialData['Catalog Tasks']?.length > 0) {
        console.log("Using provided initial data:", {
          incidents: initialData['Incidents']?.length || 0,
          catalogTasks: initialData['Catalog Tasks']?.length || 0
        });
        setExcelData(initialData);
        setFilteredData(initialData);
        setIsLoading(false);
        setDataSource('initial');
        return;
      }
      
      try {
        console.log("Loading data from JSON source");
        const data = await loadJsonData();
        console.log("Loaded JSON data:", {
          incidents: data['Incidents']?.length || 0,
          catalogTasks: data['Catalog Tasks']?.length || 0
        });
        
        // Check if we got actual data
        const hasRealData = data['Incidents']?.length > 0 || data['Catalog Tasks']?.length > 0;
        
        if (!hasRealData) {
          console.warn("JSON files are empty or couldn't be parsed, no data available");
          toast.warning("JSON files are empty or couldn't be parsed, no data available");
          setDataSource('empty');
        } else {
          toast.success(`Loaded ${data['Incidents']?.length || 0} incidents and ${data['Catalog Tasks']?.length || 0} catalog tasks from JSON`);
          setDataSource('json');
        }
        
        setExcelData(data);
        setFilteredData(data);
        setIsLoading(false);
      } catch (error) {
        console.error("Error loading data:", error);
        toast.error("Error loading data, no data available");
        setDataSource('empty');
        setExcelData({ 'Incidents': [], 'Catalog Tasks': [] });
        setFilteredData({ 'Incidents': [], 'Catalog Tasks': [] });
        setIsLoading(false);
      }
    };
    
    loadData();
  }, [initialData, jsonData]);

  // Extract API names from tickets
  const apiNames = useMemo(() => {
    const allTickets = [
      ...(excelData['Incidents'] || []),
      ...(excelData['Catalog Tasks'] || [])
    ].filter(Boolean);
    
    const uniqueApis = new Set<string>();
    
    allTickets.forEach((ticket) => {
      if (!ticket) return;
      
      const apiName = ticket.api_name;
      if (apiName) {
        uniqueApis.add(apiName);
      }
    });
    
    return Array.from(uniqueApis);
  }, [excelData]);

  // Extract assignment groups
  const assignmentGroups = useMemo(() => {
    const groups = new Set<string>();
    
    const incidents = excelData['Incidents'] || [];
    incidents.forEach(incident => {
      if (!incident) return;
      
      if (incident.assignment_group) {
        groups.add(incident.assignment_group);
      } else if (incident.service) {
        groups.add(incident.service);
      }
    });
    
    const catalogTasks = excelData['Catalog Tasks'] || [];
    catalogTasks.forEach(task => {
      if (!task) return;
      
      if (task.assignment_group) {
        groups.add(task.assignment_group);
      } else if (task.service) {
        groups.add(task.service);
      }
    });
    
    return Array.from(groups).filter(group => group);
  }, [excelData]);

  // Filter data based on selected filters
  useEffect(() => {
    if (Object.keys(excelData).length === 0) return;
    
    const fromDate = dateRange?.from || subDays(new Date(), parseInt(timeRange));
    const toDate = dateRange?.to || new Date();

    const filtered: { [key: string]: any[] } = {};
    
    Object.entries(excelData).forEach(([sheetName, data]) => {
      if (!data || !Array.isArray(data)) {
        filtered[sheetName] = [];
        return;
      }
      
      // Apply ticket type filter
      if (ticketType !== "all") {
        if (ticketType === "incidents" && sheetName !== "Incidents") {
          filtered[sheetName] = [];
          return;
        }
        if (ticketType === "tasks" && sheetName !== "Catalog Tasks") {
          filtered[sheetName] = [];
          return;
        }
      }
      
      let filteredItems = data.filter(item => {
        if (!item) return false;
        
        // Parse date from various possible date fields
        let openedDateStr = item.opened || item.created || item.opened_at || item.created_at || item.Opened;
        if (!openedDateStr) return true; // Include if no date found

        // Parse the date, accepting various formats
        let itemDate: Date;
        try {
          if (typeof openedDateStr === 'string') {
            // Try parsing as ISO string first
            itemDate = parseISO(openedDateStr);
            
            // If invalid, try regular Date constructor
            if (isNaN(itemDate.getTime())) {
              itemDate = new Date(openedDateStr);
            }
          } else if (openedDateStr instanceof Date) {
            itemDate = openedDateStr;
          } else {
            return true; // Include if date format is unknown
          }
          
          if (isNaN(itemDate.getTime())) return true; // Include if date is invalid
        } catch (e) {
          return true; // Include if date parsing fails
        }
        
        const isWithinDateRange = (isAfter(itemDate, fromDate) || itemDate.getTime() === fromDate.getTime()) && 
                                 (isBefore(itemDate, toDate) || itemDate.getTime() === toDate.getTime());
        
        // Apply API filter
        let matchesApi = true;
        if (selectedApi !== "all") {
          matchesApi = item.api_name === selectedApi;
        }
        
        // Apply priority filter
        let matchesPriority = true;
        if (priority !== "all" && item.priority) {
          matchesPriority = item.priority.toString().toLowerCase().includes(priority.toLowerCase());
        }
        
        // Apply assignment group filter
        let matchesAssignmentGroup = true;
        if (assignmentGroup !== "all") {
          const group = item.assignment_group || item.service || "";
          matchesAssignmentGroup = group === assignmentGroup;
        }
        
        return isWithinDateRange && matchesApi && matchesPriority && matchesAssignmentGroup;
      });
      
      filtered[sheetName] = filteredItems;
    });
    
    console.log("Filtered data:", {
      incidents: filtered['Incidents']?.length || 0,
      catalogTasks: filtered['Catalog Tasks']?.length || 0
    });
    
    setFilteredData(filtered);
  }, [excelData, selectedApi, timeRange, ticketType, priority, assignmentGroup, dateRange]);

  return {
    filteredData,
    excelData,
    isLoading,
    dataSource,
    filters: {
      selectedApi,
      setSelectedApi,
      timeRange,
      setTimeRange,
      ticketType,
      setTicketType,
      priority,
      setPriority,
      assignmentGroup,
      setAssignmentGroup,
      dateRange,
      setDateRange
    },
    apiNames,
    assignmentGroups
  };
}
